import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkles, Phone, CheckCircle2, Gift, Lock, Download, Delete } from "lucide-react";
import { useHideEmergentBadge } from "@/hooks/useHideEmergentBadge";

const POLL_INTERVAL_MS = 2000;

export default function KioskPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  // Install button only shows when merchant opens /kiosk/<slug>?setup=1
  // during initial tablet setup. Prevents customers from tapping it by mistake.
  const setupMode = searchParams.get("setup") === "1";
  const [business, setBusiness] = useState(null);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [askName, setAskName] = useState(false);
  // Restore active session from sessionStorage if the customer's tab refreshed
  // mid-checkin (e.g. accidental pull-to-refresh on Android). Scoped to slug so
  // sessions from one kiosk don't leak into another.
  const sessionKey = slug ? `sparkloop-kiosk-session:${slug}` : null;
  const [session, setSession] = useState(() => {
    if (!sessionKey) return null;
    try {
      const raw = window.sessionStorage.getItem(sessionKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [submitting, setSubmitting] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  // Promotions / ads shown on the kiosk
  const [ads, setAds] = useState([]);
  const [adIndex, setAdIndex] = useState(0);
  const [isIdle, setIsIdle] = useState(false);
  // SMS opt-in: null = not asked yet, true/false = picked
  const [smsOptIn, setSmsOptIn] = useState(null);
  const [smsBusy, setSmsBusy] = useState(false);

  // Persist the session whenever it changes so a tablet pull-to-refresh
  // doesn't kick the customer back to the phone screen.
  useEffect(() => {
    if (!sessionKey) return;
    try {
      if (session) window.sessionStorage.setItem(sessionKey, JSON.stringify(session));
      else window.sessionStorage.removeItem(sessionKey);
    } catch { /* quota / private mode — ignore */ }
  }, [session, sessionKey]);

  // Load business info + lock the tab into kiosk mode (hides platform badge, disables context menu etc.)
  useHideEmergentBadge();
  useEffect(() => {
    document.body.classList.add("kiosk-mode");
    document.documentElement.classList.add("kiosk-mode");
    const blockContextMenu = (e) => e.preventDefault();
    document.addEventListener("contextmenu", blockContextMenu);

    // Prevent iOS swipe-back from leaving the kiosk: push a sentinel history
    // entry so swipe-back just stays on /kiosk/<slug> instead of going to /login.
    // Re-push on every popstate so it's a permanent trap.
    const trap = () => {
      window.history.pushState({ kiosk: true }, "", window.location.href);
    };
    trap();
    window.addEventListener("popstate", trap);

    return () => {
      document.body.classList.remove("kiosk-mode");
      document.documentElement.classList.remove("kiosk-mode");
      document.removeEventListener("contextmenu", blockContextMenu);
      window.removeEventListener("popstate", trap);
    };
  }, []);

  // Swap the global PWA manifest for a per-kiosk manifest so 'Add to Home Screen'
  // creates an icon that opens THIS kiosk directly (not the app's login page).
  //
  // CRITICAL: We must DETACH the default /manifest.json link, not just append.
  // If both links coexist, iOS/Chrome pick the first manifest they encounter
  // (the default one with start_url=/), and the home-screen icon opens the
  // landing page / sign-in page instead of the kiosk.
  useEffect(() => {
    if (!slug || !business) return;
    const head = document.head;

    // Detach (don't delete) all existing manifest links so we can restore them
    // when leaving the kiosk page.
    const prevManifests = Array.from(head.querySelectorAll('link[rel="manifest"]'));
    prevManifests.forEach((el) => el.remove());

    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = `${process.env.REACT_APP_BACKEND_URL}/api/public/business/${slug}/kiosk-manifest.json`;
    link.id = "kiosk-manifest";
    head.appendChild(link);

    // Update theme color to match the business brand so the iOS status bar
    // and Android task switcher render in-brand when launched from home.
    const brand = business.brand_color || "#002FA7";
    const prevTheme = head.querySelector('meta[name="theme-color"]');
    const prevThemeContent = prevTheme?.getAttribute("content");
    if (prevTheme) prevTheme.setAttribute("content", brand);

    // iOS standalone tags — make the home-screen icon open fullscreen, no Safari chrome.
    // iOS does NOT fully honor the web manifest; it reads these meta tags AND
    // bookmarks the CURRENT URL when "Add to Home Screen" is tapped. We must
    // ensure the user is on /kiosk/<slug> at that moment for the bookmark to
    // be correct.
    const ensureMeta = (name, content, id) => {
      let el = head.querySelector(`meta[name="${name}"]`);
      const prevContent = el?.getAttribute("content");
      if (!el) {
        el = document.createElement("meta");
        el.name = name;
        el.id = id;
        head.appendChild(el);
      }
      el.setAttribute("content", content);
      return { el, prevContent, wasCreated: !prevContent };
    };

    const capable = ensureMeta("apple-mobile-web-app-capable", "yes", "kiosk-ios-capable");
    const mobile = ensureMeta("mobile-web-app-capable", "yes", "kiosk-mobile-capable");
    const title = ensureMeta("apple-mobile-web-app-title", `${business.name}`.slice(0, 16), "kiosk-ios-title");

    return () => {
      head.querySelector("#kiosk-manifest")?.remove();
      prevManifests.forEach((el) => head.appendChild(el));
      if (prevTheme && prevThemeContent !== undefined) prevTheme.setAttribute("content", prevThemeContent);
      // Restore previous meta contents (or remove if we created them).
      [capable, mobile, title].forEach(({ el, prevContent, wasCreated }) => {
        if (wasCreated) {
          el.remove();
        } else if (prevContent !== undefined) {
          el.setAttribute("content", prevContent);
        }
      });
    };
  }, [slug, business]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/business/${slug}`);
        setBusiness(data.business);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, [slug]);

  // Fetch active promotions. Refetch every 60s so merchant changes
  // propagate to a long-running kiosk without a reload.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    const fetchAds = async () => {
      try {
        const { data } = await api.get(`/public/business/${slug}/kiosk-ads`);
        if (!cancelled) setAds(Array.isArray(data) ? data : []);
      } catch {
        // ignore — kiosk should keep running even if ads fail
      }
    };
    fetchAds();
    const id = setInterval(fetchAds, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [slug]);

  // Rotate ads. Each ad stays on screen for its own `duration_seconds`.
  // We schedule the next switch using the CURRENT ad's duration, so timings
  // are honored exactly per-ad (not a fixed interval).
  useEffect(() => {
    setAdIndex(0);
  }, [ads]);

  useEffect(() => {
    if (ads.length <= 1) return;
    const current = ads[adIndex];
    const seconds = Math.max(2, Math.min(120, Number(current?.duration_seconds) || 6));
    const id = setTimeout(() => {
      setAdIndex((i) => (i + 1) % ads.length);
    }, seconds * 1000);
    return () => clearTimeout(id);
  }, [ads, adIndex]);

  // Idle detection: after 30s of no user interaction AND no active session,
  // enter idle mode (full-screen slideshow). Any touch/tap exits.
  useEffect(() => {
    if (session) { setIsIdle(false); return; }
    let timer;
    const reset = () => {
      setIsIdle(false);
      clearTimeout(timer);
      timer = setTimeout(() => setIsIdle(true), 30000);
    };
    reset();
    const events = ["pointerdown", "keydown", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [session]);

  // Capture Android Chrome's install prompt so we can offer a one-tap
  // "Install on this tablet" button. iOS Safari doesn't fire this; we
  // hide the button there. Also detect if we're already running standalone
  // (PWA mode) so we don't show the button after install.
  useEffect(() => {
    const isStandaloneNow =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.matchMedia?.("(display-mode: fullscreen)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(isStandaloneNow);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    const onInstalled = () => {
      setInstallPrompt(null);
      setIsStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    try {
      installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        toast.success("Installing… look for the icon on your home screen");
      }
    } catch {
      // user dismissed — no toast
    } finally {
      setInstallPrompt(null);
    }
  };

  // Poll session while active — watches for points updates AND end-of-session signal
  useEffect(() => {
    if (!session?.session_id) return;
    const tick = async () => {
      try {
        const { data } = await api.get(
          `/public/business/${slug}/kiosk-session/${session.session_id}`
        );
        if (data.status === "ended" || data.status === "done") {
          reset();
          return;
        }
        setSession((s) => (s ? {
          ...s,
          points: data.points,
          visits: data.visits,
          requested_reward_id: data.requested_reward_id || null,
          history: data.history || [],
        } : s));
      } catch {
        // ignore transient errors
      }
    };
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [session?.session_id, slug]);

  const requestReward = async (reward) => {
    if (!session) return;
    try {
      await api.post(
        `/public/business/${slug}/kiosk-session/${session.session_id}/request-reward`,
        { reward_id: reward.id },
      );
      setSession((s) => s ? { ...s, requested_reward_id: reward.id } : s);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cancelRequest = async () => {
    if (!session) return;
    try {
      await api.post(
        `/public/business/${slug}/kiosk-session/${session.session_id}/request-reward`,
        { reward_id: "" },
      );
      setSession((s) => s ? { ...s, requested_reward_id: null } : s);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reset = () => {
    setPhone("");
    setName("");
    setAskName(false);
    setSession(null);
    setSmsOptIn(null);
  };

  const setSmsPreference = async (optIn) => {
    if (!session) return;
    setSmsBusy(true);
    try {
      await api.post(`/public/business/${slug}/sms-opt-in`, {
        phone: session.customer.phone,
        business_slug: slug,
        opt_in: optIn,
      });
      setSmsOptIn(optIn);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSmsBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (phone.replace(/\D/g, "").length < 7) {
      toast.error("Enter a valid phone");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/business/${slug}/kiosk-checkin`, {
        phone,
        name: name || undefined,
      });
      // First-time-at-this-counter customer (visits == 0). Ask them to type or
      // confirm their name so we don't display a stale imported name to the
      // wrong person. If we have an imported name on file, pre-fill it — they
      // can accept with one tap or edit it right in place.
      if (data.is_new && !name) {
        if (data.customer?.name) setName(data.customer.name);
        setAskName(true);
        setSubmitting(false);
        return;
      }
      setSession(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!business) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f9fb]">
        <div className="text-sm uppercase tracking-[0.2em] text-[#002fa7] font-semibold">Loading…</div>
      </div>
    );
  }

  const brand = business.brand_color || "#002FA7";
  const activeAds = ads; // already filtered server-side
  const currentAd = activeAds[adIndex] || null;
  // Show side panel only on tablets/desktops, only when NOT in session, and only when we have ads.
  const showSidePanel = !session && activeAds.length > 0;

  return (
    <div
      className={
        // When checked in, allow the page to grow & scroll so the recent
        // activity is reachable. When not in session, lock to viewport so
        // the form + ads always fit cleanly.
        session
          ? "min-h-screen flex flex-col"
          : "min-h-screen h-screen overflow-hidden flex flex-col"
      }
      style={{ backgroundColor: brand }}
    >
      <div
        className={
          session
            ? "flex-1 flex items-start justify-center p-4 sm:p-6"
            : "flex-1 min-h-0 flex items-center justify-center p-4 sm:p-6 overflow-hidden"
        }
      >
        <div
          className={
            session
              ? "w-full max-w-2xl max-h-full"
              : showSidePanel
                ? "w-full max-w-6xl max-h-full grid lg:grid-cols-[1fr_minmax(0,420px)] gap-4 sm:gap-6 items-stretch"
                : "w-full max-w-xl max-h-full"
          }
        >
          {/* Side panel ad — shows on the LEFT on landscape tablets/desktops during check-in.
              `h-full` + `min-h-0` make it match the form card's height (via items-stretch),
              and the image inside uses `object-cover` to crop instead of overflow. */}
          {showSidePanel && currentAd && (
            <div
              data-testid="kiosk-ad-side"
              className="hidden lg:flex bg-white/95 rounded-3xl overflow-hidden flex-col h-full min-h-0"
            >
              <div className="flex-1 min-h-0">
                <KioskAdView ad={currentAd} brand={brand} fill />
              </div>
              {activeAds.length > 1 && (
                <div className="flex justify-center gap-1.5 py-3 flex-shrink-0">
                  {activeAds.map((_, i) => (
                    <span
                      key={i}
                      className="w-1.5 h-1.5 rounded-full transition-all"
                      style={{
                        backgroundColor: i === adIndex ? brand : "#e5e5eb",
                        width: i === adIndex ? 18 : 6,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {session ? (
            <div data-testid="kiosk-balance" className="bg-white rounded-3xl p-8 sm:p-10 text-center fade-up">
              <div className="w-16 h-16 rounded-full bg-[#009e60]/10 text-[#009e60] flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8" strokeWidth={2} />
              </div>
              <div className="text-xs uppercase tracking-[0.24em] text-[#002fa7] font-semibold mb-2">
                You're checked in
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[0.9] mb-4">
                Hi{session.customer.name ? `, ${session.customer.name.split(" ")[0]}` : ""}.
              </h1>
              <div className="bg-[#f9f9fb] rounded-2xl p-5 mb-6">
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#5a5a65] font-semibold">
                  Your balance
                </div>
                <div className="font-display text-6xl font-extrabold" style={{ color: brand }}>
                  {session.points.toLocaleString()}
                </div>
                <div className="text-xs text-[#5a5a65] uppercase tracking-[0.2em] mt-1">pts</div>
              </div>

              {/* Rewards picker */}
              {session.rewards && session.rewards.length > 0 && (
                <RewardsPicker
                  rewards={session.rewards}
                  customerPoints={session.points}
                  requestedRewardId={session.requested_reward_id}
                  brand={brand}
                  onRequest={requestReward}
                  onCancel={cancelRequest}
                />
              )}

              {/* SMS opt-in — asked once per check-in session */}
              {smsOptIn === null && (
                <div className="mt-6 bg-[#f9f9fb] rounded-2xl p-4 sm:p-5 border border-[#e5e5eb]" data-testid="kiosk-sms-optin">
                  <div className="text-sm sm:text-base font-display font-extrabold text-[#0a0a0a] mb-1">
                    Get deals via text?
                  </div>
                  <p className="text-xs text-[#5a5a65] mb-3 leading-snug">
                    Tap yes and we'll text you about specials and free rewards. Standard rates apply. STOP to unsubscribe.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      data-testid="kiosk-sms-yes"
                      disabled={smsBusy}
                      onClick={() => setSmsPreference(true)}
                      className="h-11 rounded-xl font-semibold text-sm text-white disabled:opacity-50"
                      style={{ backgroundColor: brand }}
                    >
                      Yes, text me
                    </button>
                    <button
                      data-testid="kiosk-sms-no"
                      disabled={smsBusy}
                      onClick={() => setSmsPreference(false)}
                      className="h-11 rounded-xl font-semibold text-sm bg-white border border-[#e5e5eb] text-[#0a0a0a] disabled:opacity-50"
                    >
                      No thanks
                    </button>
                  </div>
                </div>
              )}
              {smsOptIn === true && (
                <div className="mt-6 text-xs uppercase tracking-[0.2em] font-semibold text-[#009e60]" data-testid="kiosk-sms-confirmed">
                  ✓ You're subscribed to deals
                </div>
              )}

              {!session.requested_reward_id && (
                <p className="text-[#5a5a65] text-sm mt-6">
                  Please wait while the cashier adds or redeems your points.
                </p>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-5 sm:p-6 lg:p-7 flex flex-col h-full min-h-0 overflow-hidden">
              {/* Mobile/portrait inline ad (shows above the form on small screens). */}
              {showSidePanel && currentAd && (
                <div data-testid="kiosk-ad-inline" className="lg:hidden -mx-5 sm:-mx-6 -mt-5 sm:-mt-6 mb-4 overflow-hidden rounded-t-3xl">
                  <KioskAdView ad={currentAd} brand={brand} compact />
                </div>
              )}
              {/* Business pill at the top — colored chip in brand color */}
              <div
                className="inline-flex self-start items-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-full mb-3"
                style={{ backgroundColor: `${brand}15`, color: brand }}
              >
                <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> {business.name}
              </div>

              {askName ? (
                <>
                  <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[0.92] mb-2">
                    Almost there.
                  </h1>
                  <p className="text-[#5a5a65] text-sm sm:text-base mb-4">
                    What should we call you?
                  </p>
                </>
              ) : (
                <>
                  <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[0.9] mb-2">
                    Earn points.{" "}
                    <span style={{ color: brand }}>Get rewarded.</span>
                  </h1>
                  <p className="text-[#0a0a0a]/70 text-sm mb-4 leading-relaxed">
                    Drop your number to start earning — no app, no plastic card.{" "}
                    <span className="font-semibold text-[#0a0a0a]">Just rewards.</span>
                  </p>
                </>
              )}
              <form onSubmit={submit} className="space-y-3 flex-1 flex flex-col min-h-0" data-testid="kiosk-form">
                {!askName ? (
                  <>
                    <div className="relative">
                      <Phone className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-[#5a5a65]" />
                      <input
                        data-testid="kiosk-phone-input"
                        type="tel"
                        inputMode="none"
                        readOnly
                        value={formatPhoneDisplay(phone)}
                        placeholder="+1 (555) 123-4567"
                        onFocus={(e) => e.target.blur()}
                        className="flex w-full h-12 sm:h-14 pl-12 pr-4 rounded-2xl border-2 border-[#e5e5eb] text-xl sm:text-2xl font-mono-data bg-white tabular-nums tracking-wider focus:border-[color:var(--kiosk-brand)] transition-colors"
                        style={{ "--kiosk-brand": brand }}
                        aria-label="Phone number"
                      />
                    </div>
                    <Numpad
                      brand={brand}
                      onDigit={(d) => setPhone((p) => (p.replace(/\D/g, "").length >= 14 ? p : p + d))}
                      onBackspace={() => setPhone((p) => p.slice(0, -1))}
                      canSubmit={phone.replace(/\D/g, "").length >= 7}
                      submitting={submitting}
                      onSubmit={submit}
                    />
                  </>
                ) : (
                  <>
                    <div
                      data-testid="kiosk-name-display"
                      className="h-14 rounded-2xl border-2 border-[#e5e5eb] bg-white text-xl px-4 flex items-center font-mono-data"
                    >
                      {name || <span className="text-[#a0a0a8]">Tap letters below…</span>}
                      <span className="ml-0.5 inline-block w-0.5 h-6 bg-[#0a0a0a] animate-pulse" />
                    </div>
                    <OnScreenKeyboard
                      brand={brand}
                      value={name}
                      onKey={(k) => setName((n) => k === "BACKSPACE" ? n.slice(0, -1) : k === "SPACE" ? n + " " : n.length < 24 ? n + k : n)}
                    />
                    <Button
                      data-testid="kiosk-submit-btn"
                      type="submit"
                      disabled={submitting || !name.trim()}
                      className="w-full h-12 sm:h-14 text-base sm:text-lg font-bold rounded-2xl text-white disabled:opacity-40 flex-shrink-0 shadow-lg shadow-black/10 transition-transform active:scale-[0.98]"
                      style={{ backgroundColor: brand }}
                    >
                      {submitting ? "Checking in…" : "I'm in →"}
                    </Button>
                  </>
                )}
              </form>
            </div>
          )}
        </div>
      </div>
      <div className="py-4 text-center text-xs uppercase tracking-[0.24em] text-white/70">
        Powered by <span className="font-bold text-white">SparkLoop</span>
      </div>

      {/* Idle full-screen slideshow — fires after 30s of no input AND no active session.
          Any tap dismisses immediately so customers can start a check-in. */}
      {isIdle && !session && ads.length > 0 && currentAd && (
        <div
          data-testid="kiosk-idle-overlay"
          onClick={() => setIsIdle(false)}
          onTouchStart={() => setIsIdle(false)}
          className="fixed inset-0 z-40 flex items-center justify-center cursor-pointer animate-[fadeIn_0.4s_ease-out]"
          style={{ backgroundColor: brand }}
        >
          <div className="w-full max-w-5xl mx-auto p-6 sm:p-10">
            <div className="bg-white rounded-3xl overflow-hidden shadow-2xl">
              <KioskAdView ad={currentAd} brand={brand} large />
            </div>
            <div className="text-center mt-6">
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-5 py-2.5 rounded-full text-white text-sm font-semibold uppercase tracking-[0.16em]">
                Tap anywhere to check in
              </div>
            </div>
            {ads.length > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {ads.map((_, i) => (
                  <span
                    key={i}
                    className="h-1.5 rounded-full transition-all bg-white"
                    style={{ width: i === adIndex ? 28 : 8, opacity: i === adIndex ? 1 : 0.4 }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* One-tap install on Android Chrome — only shown during setup mode
          (kiosk URL has ?setup=1) so customers can't tap it by mistake.
          Auto-hidden on iOS, after install, or in normal kiosk use. */}
      {setupMode && installPrompt && !isStandalone && (
        <button
          data-testid="kiosk-install-btn"
          onClick={handleInstall}
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-3 rounded-full bg-white/95 backdrop-blur shadow-lg text-[#0a0a0a] text-sm font-semibold uppercase tracking-wider hover:scale-105 active:scale-95 transition-transform"
        >
          <Download className="w-4 h-4" />
          Install on this tablet
        </button>
      )}
    </div>
  );
}


function RewardsPicker({ rewards, customerPoints, requestedRewardId, brand, onRequest, onCancel }) {
  // If a reward is already requested, show a single confirmation panel instead of the grid.
  if (requestedRewardId) {
    const r = rewards.find((x) => x.id === requestedRewardId);
    if (!r) return null;
    return (
      <div
        data-testid="kiosk-reward-pending"
        className="rounded-2xl p-5 mb-4 text-left"
        style={{ backgroundColor: `${brand}10`, borderWidth: 1, borderColor: `${brand}40` }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div
              className="text-[10px] uppercase tracking-[0.24em] font-semibold mb-1"
              style={{ color: brand }}
            >
              Sent to cashier
            </div>
            <div className="font-display text-2xl font-extrabold leading-tight">{r.title}</div>
            {r.description && (
              <div className="text-sm text-[#5a5a65] mt-1">{r.description}</div>
            )}
            <div className="text-xs text-[#5a5a65] mt-2 font-mono-data">
              {r.points_cost} pts · awaiting approval
            </div>
          </div>
          <Gift className="w-6 h-6 flex-shrink-0" style={{ color: brand }} />
        </div>
        <button
          data-testid="kiosk-reward-cancel"
          onClick={onCancel}
          className="mt-3 text-xs uppercase tracking-wider font-semibold text-[#5a5a65] hover:text-[#0a0a0a]"
        >
          Change my mind
        </button>
      </div>
    );
  }

  return (
    <div className="mb-2" data-testid="kiosk-rewards-grid">
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#5a5a65] font-semibold mb-3 text-left">
        Rewards available
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {rewards.map((r) => {
          const canAfford = customerPoints >= r.points_cost;
          return (
            <button
              key={r.id}
              data-testid={`kiosk-reward-${r.id}`}
              onClick={() => canAfford && onRequest(r)}
              disabled={!canAfford}
              className={`relative rounded-2xl p-4 text-left transition-all ${
                canAfford
                  ? "bg-white border-2 hover:scale-[1.02] active:scale-[0.98] cursor-pointer shadow-sm hover:shadow-md"
                  : "bg-[#f5f5f7] border-2 border-[#e5e5eb] opacity-50 cursor-not-allowed"
              }`}
              style={canAfford ? { borderColor: `${brand}40` } : {}}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-extrabold text-lg leading-tight truncate">
                    {r.title}
                  </div>
                  {r.description && (
                    <div className="text-xs text-[#5a5a65] mt-0.5 line-clamp-2">
                      {r.description}
                    </div>
                  )}
                </div>
                {!canAfford && (
                  <Lock className="w-4 h-4 text-[#5a5a65] flex-shrink-0 mt-0.5" />
                )}
              </div>
              <div className="flex items-end justify-between mt-3 pt-2 border-t border-[#e5e5eb]">
                <span
                  className="font-mono-data text-sm font-semibold"
                  style={canAfford ? { color: brand } : { color: "#5a5a65" }}
                >
                  {r.points_cost} pts
                </span>
                {canAfford ? (
                  <span
                    className="text-[10px] uppercase tracking-wider font-bold"
                    style={{ color: brand }}
                  >
                    Tap to claim
                  </span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-[#5a5a65]">
                    {(r.points_cost - customerPoints).toLocaleString()} pts to go
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


// Single promotion card. Used in 4 places:
// - Side panel (`fill`) during check-in on landscape tablets — fills parent height
// - Inline compact strip (`compact`) on portrait/phones
// - Full-screen idle slideshow (`large`)
// - Default fallback (kept for safety) — uses 4:5 aspect
function KioskAdView({ ad, brand, compact = false, large = false, fill = false }) {
  let aspect = "aspect-[4/5]";
  if (fill) aspect = "h-full";
  else if (large) aspect = "aspect-[16/9]";
  else if (compact) aspect = "aspect-[5/2]";

  const headlineSize = large
    ? "text-4xl sm:text-5xl"
    : compact
      ? "text-xl sm:text-2xl"
      : "text-2xl sm:text-3xl";
  const bodySize = large ? "text-lg sm:text-xl" : "text-sm";

  return (
    <div className={`relative ${aspect} w-full overflow-hidden`}>
      {ad.image_data ? (
        <img
          src={ad.image_data}
          alt={ad.headline || "Promotion"}
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(135deg, ${brand} 0%, ${brand}cc 100%)` }}
        />
      )}
      {(ad.headline || ad.body) && (
        <div
          className={`absolute inset-0 flex flex-col justify-end p-5 sm:p-7 ${
            ad.image_data ? "bg-gradient-to-t from-black/75 via-black/10 to-transparent" : ""
          }`}
        >
          {ad.headline && (
            <div
              className={`font-display font-extrabold tracking-tight leading-[0.95] ${headlineSize} ${
                ad.image_data ? "text-white" : "text-white"
              }`}
            >
              {ad.headline}
            </div>
          )}
          {ad.body && (
            <div
              className={`mt-2 ${bodySize} ${ad.image_data ? "text-white/90" : "text-white/95"}`}
            >
              {ad.body}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// Format a transaction timestamp as "Today 2:35 PM" / "Yesterday" / "May 12".
function formatHistoryDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (isToday) return `Today ${time}`;
  if (isYesterday) return `Yesterday ${time}`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

// Format raw digits as a US phone for display: "5551234567" -> "(555) 123-4567".
// For 11 digits starting with 1, shows "+1 (555) 123-4567". Falls back to raw
// for anything that doesn't match a US shape.
function formatPhoneDisplay(raw) {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length <= 3) return `+1 (${digits}`;
  if (digits.length <= 6) return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10) return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === "1")
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return `+${digits}`;
}

// Big-tap numeric keypad used on the kiosk so customers don't need the
// system keyboard. 3x4 grid:
//   1 2 3
//   4 5 6
//   7 8 9
//   ⌫ 0 ✓  ← bottom row: backspace, zero, submit (always reachable)
function OnScreenKeyboard({ brand, value, onKey }) {
  // Compact QWERTY tuned for first-name entry on a tablet. Shift toggles
  // upper/lower. Auto-shift on first key like a phone.
  const [shift, setShift] = useState(true);
  const rows = [
    ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
    ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
    ["Z", "X", "C", "V", "B", "N", "M"],
  ];
  const showShift = shift || value.length === 0;

  const tap = (k) => {
    onKey(showShift ? k : k.toLowerCase());
    if (shift && value.length > 0) setShift(false);
    else if (shift && value.length === 0) setShift(false);
  };

  const keyBase = "h-12 sm:h-14 rounded-xl bg-white border border-[#e5e5eb] text-base sm:text-lg font-semibold text-[#0a0a0a] active:scale-95 active:bg-[#f0f0f4] transition select-none touch-manipulation";

  return (
    <div className="space-y-1.5 sm:space-y-2 select-none" data-testid="kiosk-keyboard">
      {rows.map((row, i) => (
        <div
          key={i}
          className="grid gap-1.5 sm:gap-2"
          style={{ gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))` }}
        >
          {row.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => tap(k)}
              data-testid={`kbd-${k.toLowerCase()}`}
              className={keyBase}
            >
              {showShift ? k : k.toLowerCase()}
            </button>
          ))}
        </div>
      ))}
      <div className="grid grid-cols-[1.4fr_3fr_1.4fr] gap-1.5 sm:gap-2">
        <button
          type="button"
          data-testid="kbd-shift"
          onClick={() => setShift((s) => !s)}
          className={`${keyBase} ${showShift ? "text-white" : ""}`}
          style={showShift ? { backgroundColor: brand, borderColor: brand } : {}}
        >
          ⇧
        </button>
        <button
          type="button"
          data-testid="kbd-space"
          onClick={() => onKey("SPACE")}
          className={`${keyBase} text-xs uppercase tracking-[0.2em] text-[#5a5a65]`}
        >
          Space
        </button>
        <button
          type="button"
          data-testid="kbd-backspace"
          onClick={() => onKey("BACKSPACE")}
          className={keyBase}
        >
          ⌫
        </button>
      </div>
    </div>
  );
}


function Numpad({ onDigit, onBackspace, brand, canSubmit, submitting, onSubmit }) {
  return (
    <div
      data-testid="kiosk-numpad"
      className="grid grid-cols-3 gap-2 select-none flex-1 min-h-0"
    >
      {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k) => (
        <button
          key={k}
          type="button"
          data-testid={`kiosk-numpad-${k}`}
          onClick={() => onDigit(k)}
          className="min-h-[44px] rounded-xl bg-white border-2 border-[#e5e5eb] hover:border-[#0a0a0a] active:scale-95 transition font-display text-xl sm:text-2xl font-extrabold"
          style={{ color: brand }}
        >
          {k}
        </button>
      ))}
      <button
        type="button"
        data-testid="kiosk-numpad-back"
        onClick={onBackspace}
        className="min-h-[44px] rounded-xl bg-[#f0f0f4] hover:bg-[#e5e5eb] active:scale-95 transition flex items-center justify-center text-[#0a0a0a]"
        aria-label="Backspace"
      >
        <Delete className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>
      <button
        type="button"
        data-testid="kiosk-numpad-0"
        onClick={() => onDigit("0")}
        className="min-h-[44px] rounded-xl bg-white border-2 border-[#e5e5eb] hover:border-[#0a0a0a] active:scale-95 transition font-display text-xl sm:text-2xl font-extrabold"
        style={{ color: brand }}
      >
        0
      </button>
      <button
        type={onSubmit ? "submit" : "button"}
        data-testid="kiosk-submit-btn"
        onClick={onSubmit ? undefined : undefined}
        disabled={!canSubmit || submitting}
        className="min-h-[44px] rounded-xl active:scale-95 transition flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-lg shadow-black/10 disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed"
        style={{ backgroundColor: brand }}
        aria-label="Check me in"
      >
        {submitting ? "…" : "Check in →"}
      </button>
    </div>
  );
}
